import { BrowserRouter, Route, Routes } from 'react-router-dom';

import React, { Suspense } from 'react';

import { AuthProvider } from '@/hooks/use-auth';
import { ThemeProvider } from '@/hooks/use-theme';

import { AdminAuthGuard } from '@/components/AdminAuthGuard';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Skeleton } from '@/components/ui/skeleton';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryProvider } from '@/providers/QueryProvider';
import { ClassificationDebug } from '@/components/ClassificationDebug';

// Lazy load page components
const Index = React.lazy(() => import('./pages/Index'));
const AuthPage = React.lazy(() => import('./pages/Auth'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const History = React.lazy(() => import('./pages/History'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPostDetail = React.lazy(() => import('./pages/BlogPostDetail'));
// Legal pages
const PrivacyPolicy = React.lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsConditions = React.lazy(() => import('./pages/legal/TermsConditions'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
// Lazy load admin pages
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const ManagePosts = React.lazy(() => import('./pages/admin/ManagePosts'));
const PostEditor = React.lazy(() => import('./pages/admin/PostEditor'));
const R2Test = React.lazy(() => import('./pages/admin/R2Test'));
const ManageModels = React.lazy(() => import('./pages/admin/ManageModels'));
const ModelEditor = React.lazy(() => import('./pages/admin/ModelEditor'));
const AdminPerformanceDashboard = React.lazy(() => import('./pages/admin/PerformanceDashboard'));

// Loading fallback for lazy-loaded components
const PageLoader = () => (
    <div className="p-8 flex flex-col space-y-4 w-full h-screen">
        <div className="flex justify-between items-center">
            <Skeleton className="h-10 w-1/3" />
            <Skeleton className="h-10 w-20" />
        </div>
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-[300px] w-full" />
        <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
        </div>
    </div>
);

const App = () => (
    <ErrorBoundary>
        <QueryProvider>
            <ThemeProvider defaultTheme="system" storageKey="modelmatch-theme">
                <AuthProvider>
                    <TooltipProvider>
                        <Toaster />
                        <Sonner />
                        <BrowserRouter>
                            <Suspense fallback={<PageLoader />}>
                                <Routes>
                                    {/* Public routes */}
                                    <Route path="/" element={<Index />} />
                                    <Route path="/auth" element={<AuthPage />} />
                                    <Route path="/history" element={<History />} />
                                    <Route path="/blog" element={<Blog />} />
                                    <Route path="/blog/:slug" element={<BlogPostDetail />} />
                                    <Route path="/privacy" element={<PrivacyPolicy />} />
                                    <Route path="/terms" element={<TermsConditions />} />
                                    <Route path="/faq" element={<FAQ />} />

                                    {/* Admin routes */}
                                    <Route element={<AdminAuthGuard />}>
                                        <Route path="/admin" element={<AdminDashboard />} />
                                        <Route path="/admin/posts" element={<ManagePosts />} />
                                        <Route path="/admin/posts/new" element={<PostEditor />} />
                                        <Route path="/admin/posts/edit/:id" element={<PostEditor />} />
                                        <Route path="/admin/r2-test" element={<R2Test />} />
                                        <Route path="/admin/models" element={<ManageModels />} />
                                        <Route path="/admin/models/new" element={<ModelEditor />} />
                                        <Route path="/admin/models/edit/:id" element={<ModelEditor />} />
                                        <Route path="/admin/performance" element={<AdminPerformanceDashboard />} />
                                    </Route>

                                    <Route path="*" element={<NotFound />} />
                                </Routes>
                            </Suspense>
                        </BrowserRouter>
                        {/* Debug tools only in development */}
                        {import.meta.env.VITE_ENV === "development" && <ClassificationDebug />}
                    </TooltipProvider>
                </AuthProvider>
            </ThemeProvider>
        </QueryProvider>
    </ErrorBoundary>
);

export default App;
